import React, { createContext, useContext, useState, useEffect } from 'react';
import userService from '../services/userService.js';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [familyMembers, setFamilyMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch family members from database
    const fetchFamilyMembers = async () => {
        try {
            setLoading(true);
            const users = await userService.getAllUsers();
            // Transform database users to the format expected by components
            const transformedUsers = users.map(user => ({
                id: user.id,
                name: user.display_name,
                color: user.color,
                username: user.username,
                canDoWeekendDuty: user.can_do_weekend_duty,
                role: user.role,
                isActive: user.is_active
            }));
            setFamilyMembers(transformedUsers);
        } catch (error) {
            console.error('Error fetching family members:', error);
            // Set empty array if database fails - let components handle the empty state
            setFamilyMembers([]);
        } finally {
            setLoading(false);
        }
    };

    // Simple authentication - in production you'd want proper backend auth
    const login = (username, password) => {
        // Simple demo passwords - in production use proper authentication
        const user = familyMembers.find(member =>
            member.username === username &&
            (password === 'family2024' || password === member.username + '2024')
        );

        if (user) {
            setCurrentUser(user);
            setIsAuthenticated(true);
            localStorage.setItem('currentUser', JSON.stringify(user));
            localStorage.setItem('isAuthenticated', 'true');
            return true;
        }
        return false;
    };

    const logout = () => {
        setCurrentUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('currentUser');
        localStorage.removeItem('isAuthenticated');
    };

    useEffect(() => {
        // Fetch family members from database on component mount
        fetchFamilyMembers();

        // Check if user is already logged in
        const savedUser = localStorage.getItem('currentUser');
        const savedAuth = localStorage.getItem('isAuthenticated');

        if (savedUser && savedAuth === 'true') {
            setCurrentUser(JSON.parse(savedUser));
            setIsAuthenticated(true);
        }
    }, []);

    const value = {
        isAuthenticated,
        currentUser,
        familyMembers,
        loading,
        login,
        logout,
        refreshFamilyMembers: fetchFamilyMembers
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
